import appConstants from '@appConstants';
import { TRAILER_SLOT_VALUES } from './constants';
import type { SwapBoardUpdatePayload } from './types';

export const TRAILER_UPDATE_KEYS = [
  'status',
  'moveType',
  'pickupNumber',
  'driverName',
  'driverId',
  'driverNumber',
  ...TRAILER_SLOT_VALUES.map((slot) => `${slot}SpottedDetails`),
];

export const getUpdatePickupIds = (payload?: SwapBoardUpdatePayload | null): string[] => {
  if (!payload) {
    return [];
  }

  const ids = [
    ...(Array.isArray(payload.pickupIds) ? payload.pickupIds : []),
    ...(payload.pickupId ? [payload.pickupId] : []),
  ];

  return Array.from(new Set(ids.map((id) => id?.toUpperCase?.()).filter(Boolean))) as string[];
};

/** Flattens TrailerMovesUpdated `{ success, data }` envelopes so upsert can read pickup fields. */
export const normalizeTrailerMovesUpdatedPayload = (
  payload?: SwapBoardUpdatePayload | null
): SwapBoardUpdatePayload => {
  if (!payload) {
    return {};
  }
  if (!payload.data || Object.keys(payload.data).length === 0) {
    return payload;
  }
  const { data, ...rest } = payload;
  return { ...data, ...rest };
};

export const buildPickupAssignUpdatePayload = (
  pickupData: SwapBoardUpdatePayload,
  eventName: string
): SwapBoardUpdatePayload => {
  const nested = pickupData.data ?? {};
  const pick = <K extends keyof SwapBoardUpdatePayload>(key: K) =>
    pickupData[key] ?? nested[key];
  const pickupIds =
    pickupData.pickupIds ??
    nested.pickupIds ??
    (pick('pickupId') ? [pick('pickupId') as string] : []);
  const clearDriver = eventName === appConstants.PICKUP_EVENTS.UNASSIGN_PICKUP;

  return {
    pickupIds,
    pickupId: pick('pickupId') ?? pickupIds[0],
    status: pick('status'),
    moveType: pick('moveType'),
    pickupNumber: pick('pickupNumber'),
    rmaxShipperCode: pick('rmaxShipperCode'),
    shipperName: pick('shipperName'),
    spottedTrailers: pick('spottedTrailers'),
    driverName: clearDriver ? null : pick('driverName') ?? null,
    driverId: clearDriver ? null : pick('driverId') ?? null,
    driverNumber: clearDriver ? null : pick('driverNumber') ?? null,
  };
};

export const buildPickupUpdatePayload = (
  pickupData: SwapBoardUpdatePayload
): SwapBoardUpdatePayload => {
  const nested = pickupData.data ?? {};
  const pickupIds =
    nested.pickupIds ??
    (nested.pickupId ? [nested.pickupId] : pickupData.pickupId ? [pickupData.pickupId] : []);
  const pickupId = pickupData.pickupId ?? nested.pickupId ?? pickupIds[0];

  const payload: SwapBoardUpdatePayload = {
    pickupIds: pickupIds.length ? pickupIds : undefined,
    pickupId,
  };

  TRAILER_UPDATE_KEYS.forEach((key) => {
    const value = nested[key] ?? pickupData[key];
    if (value !== undefined) {
      payload[key] = value;
    }
  });

  const shipperCode = nested.rmaxShipperCode ?? pickupData.rmaxShipperCode;
  if (shipperCode !== undefined) {
    payload.rmaxShipperCode = shipperCode;
  }

  const spottedTrailers = nested.spottedTrailers ?? pickupData.spottedTrailers;
  if (spottedTrailers !== undefined) {
    payload.spottedTrailers = spottedTrailers;
  }

  return payload;
};
